import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { NEWSLETTER_SUBSCRIPTION_API_ORIGIN } from 'constants/index';
import makeRequest from 'utils/makeRequest';
import { toggleLoginBox } from 'components/Login/redux';
import NewsletterCard from 'components_v2/common/NewsletterCard/NewsletterCard';
import NewsletterSubscriptionPopup from 'components_v2/common/NewsletterCard/NewsletterSubscriptionPopup';
import analyticsWrapper from 'helpers/analytics/analyticsWrapper';
import NewsletterBanner from './NewsletterBanner';
import s from './Newsletter.scss';

const Newsletter = props => {
  const { userData, items, isListingPage } = props;

  const [newslettersData, setNewslettersData] = useState(items);
  if (!(Array.isArray(newslettersData) && newslettersData.length > 0)) {
    return null;
  }
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [showUnsubscribeAllBtn, setShowUnsubscribeAllBtn] = useState(false);
  const [subscribedIds, setSubscribedIds] = useState([]);
  const [userSubscribedId, setUserSubscribedId] = useState('');
  const [pendingSubscription, setPendingSubscription] = useState(null);
  const [
    showRequestLimitExceedPopup,
    setShowRequestLimitExceedPopup,
  ] = useState(false);

  const defaultNewslettersValue = newslettersData.map(newsletter => ({
    serviceId: newsletter.serviceId,
    userPreference: newsletter.userPreference,
  }));

  const ga4LoginEventEmitter = (eventType = 'before_login_subscribe_click') => {
    let sectionName = '';
    if (
      typeof window !== 'undefined' &&
      window.location &&
      window.location.pathname
    ) {
      const pathArray = window.location.pathname.split('/');
      sectionName = pathArray && pathArray[1] ? pathArray[1] : '';
    }
    analyticsWrapper('ga4', {
      event: 'user_click',
      type: eventType,
      category: sectionName,
    });
  };

  const openLoginBox = userEmailid => {
    if (isListingPage) {
      ga4LoginEventEmitter('before_login_subscribe_click');
    }
    if (props.toggleLoginBox && typeof props.toggleLoginBox === 'function') {
      if (isListingPage && userEmailid) {
        props.toggleLoginBox(true, userEmailid);
      } else {
        props.toggleLoginBox(true);
      }
    }
  };
  const onSubscribe = (
    serviceId,
    userPreference,
    subscribeCallback,
    isUnsubscribe = false,
    userEmailid = null,
  ) => {
    const currentUserData = props.userData;
    const userEmail =
      currentUserData && currentUserData.getEmail
        ? currentUserData.getEmail()
        : '';
    setUserSubscribedId(serviceId);
    if ((isListingPage && !userEmailid) || (!userEmail && !isListingPage)) {
      // Store the subscription parameters to retry after login
      setPendingSubscription({
        serviceId,
        userPreference,
        subscribeCallback,
        isUnsubscribe,
        userEmailid,
      });
      openLoginBox();
      return;
    } else if (isListingPage && userEmailid && !userEmail) {
      // Store the subscription parameters to retry after login
      setPendingSubscription({
        serviceId,
        userPreference,
        subscribeCallback,
        isUnsubscribe,
        userEmailid,
      });
      openLoginBox(userEmailid);
      return;
    }
    // Get current userData from props to ensure we have the latest value
    if (isUserLoggedIn && isListingPage) {
      ga4LoginEventEmitter('after_login_subscribe_click');
    }
    const reqBody = {};
    if (isUnsubscribe) {
      reqBody.serviceId = serviceId;
    } else {
      reqBody.serviceIds = serviceId;
      reqBody.ssoCheck = true;
      reqBody.userPreference = userPreference;
    }
    reqBody.userId = userEmail;

    const serviceProvURL = `${NEWSLETTER_SUBSCRIPTION_API_ORIGIN}et-mailing-subscription/ok/${
      isUnsubscribe ? 'unsub' : 'subscription'
    }/5f5a00075651d4e45e1b67c6`;

    makeRequest
      .post(serviceProvURL, reqBody, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then(response => {
        if (response && response.data && response.data.status === 429) {
          setShowRequestLimitExceedPopup(true);
          return;
        }
        if (response && response.status === 200) {
          if (isListingPage) {
            ga4LoginEventEmitter('nl_subscribe_success');
          }
          subscribeCallback();
          setSubscribedIds(
            prevState =>
              isUnsubscribe
                ? prevState.filter(id => id !== serviceId)
                : [...prevState, serviceId],
          );
        }
      })
      .catch(error => {
        console.error('error:', error);
      });
  };

  const executePendingSubscription = () => {
    if (pendingSubscription) {
      const {
        serviceId,
        userPreference,
        subscribeCallback,
        isUnsubscribe,
        userEmailid,
      } = pendingSubscription;
      // Clear pending subscription first
      setPendingSubscription(null);
      // Execute the subscription with current userData
      onSubscribe(
        serviceId,
        userPreference,
        subscribeCallback,
        isUnsubscribe,
        userEmailid,
      );
    }
  };

  const getNewsletterStatus = () => {
    const serviceProvURL = `${NEWSLETTER_SUBSCRIPTION_API_ORIGIN}et-mailing-subscription/ok/chkStatus/5f5a00075651d4e45e1b67c6`;
    const reqBody = {
      ssoId: userData.getEmail(),
    };
    makeRequest
      .post(serviceProvURL, reqBody, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then(response => {
        if (response && response.status === 200) {
          const responseData = response.data;
          if (
            responseData &&
            responseData.subscriptionStatus &&
            responseData.subscriptionStatus.subscriptions
          ) {
            const subscriptionArr =
              responseData.subscriptionStatus.subscriptions;
            if (
              subscriptionArr &&
              Array.isArray(subscriptionArr) &&
              subscriptionArr.length > 0
            ) {
              const subscribedServiceIds = [];
              subscriptionArr.forEach(subscription => {
                if (
                  subscription &&
                  subscription.subscriptionStatus === 'ACTIVE'
                ) {
                  subscribedServiceIds.push(subscription.serviceId);
                  if (subscription.userPreference) {
                    setNewslettersData(prevState => {
                      const newState = [...prevState];
                      newState.forEach(newsletter => {
                        if (newsletter.serviceId === subscription.serviceId) {
                          newsletter.userPreference =
                            subscription.userPreference;
                        }
                      });
                      return newState;
                    });
                  }
                }
              });
              setSubscribedIds(subscribedServiceIds);
              // Check if both default service IDs are subscribed
              const defaultServiceIds = defaultNewslettersValue.map(
                value => value.serviceId,
              );
              const allDefaultSubscribed = defaultServiceIds.every(id =>
                subscribedServiceIds.includes(id),
              );
              if (allDefaultSubscribed) {
                setShowUnsubscribeAllBtn(true);
              } else {
                setShowUnsubscribeAllBtn(false);
              }
              // check if userclicked newsletter is already subscribed
              if (
                userSubscribedId &&
                subscribedIds.indexOf(userSubscribedId) < 0
              ) {
                executePendingSubscription();
              }
            }
          }
        }
      })
      .catch(error => {
        console.error('error:', error);
      });
  };

  const subscribeAllNewletters = callback => {
    if (!isUserLoggedIn) {
      openLoginBox();
      return;
    }

    const userEmail = userData && userData.getEmail ? userData.getEmail() : '';
    const reqBody = {};

    reqBody.userId = userEmail;
    reqBody.ssoCheck = true;
    reqBody.isloginSignup = true;
    reqBody.preferenceSubscriptions = defaultNewslettersValue;

    const serviceProvURL = `${NEWSLETTER_SUBSCRIPTION_API_ORIGIN}et-mailing-subscription/ok/subscription/5f5a00075651d4e45e1b67c6`;

    makeRequest
      .post(serviceProvURL, reqBody, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then(response => {
        if (response && response.data && response.data.status === 429) {
          setShowRequestLimitExceedPopup(true);
          return;
        }
        if (response && response.status === 200) {
          if (isListingPage) {
            ga4LoginEventEmitter('nl_subscribe_success');
          }
          setSubscribedIds([
            ...defaultNewslettersValue.map(value => value.serviceId),
          ]);
          setShowUnsubscribeAllBtn(true);
          if (typeof callback === 'function') {
            callback();
          }
        }
      })
      .catch(error => {
        console.error('error:', error);
      });
  };

  const unsubscribeAllNewletters = callback => {
    if (!isUserLoggedIn) {
      openLoginBox();
      return;
    }
    const userEmail = userData && userData.getEmail ? userData.getEmail() : '';
    const reqBody = {};

    reqBody.userId = userEmail;

    let serviceIds = '';
    defaultNewslettersValue.forEach((value, index) => {
      if (index === 0) {
        serviceIds += value.serviceId;
      } else {
        serviceIds = `${serviceIds},${value.serviceId}`;
      }
    });

    reqBody.serviceId = serviceIds;

    const serviceProvURL = `${NEWSLETTER_SUBSCRIPTION_API_ORIGIN}et-mailing-subscription/ok/unsubscribe/5f5a00075651d4e45e1b67c6`;

    makeRequest
      .post(serviceProvURL, reqBody, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then(response => {
        if (response && response.status === 200) {
          setShowUnsubscribeAllBtn(false);
          setSubscribedIds([]);
          if (typeof callback === 'function') {
            callback();
          }
        }
      })
      .catch(error => {
        console.error('error:', error);
      });
  };

  useEffect(
    () => {
      if (userData && userData.isLoggedIn && !isUserLoggedIn) {
        setIsUserLoggedIn(true);
        getNewsletterStatus();
      }
    },
    [userData],
  );

  useEffect(
    () => {
      if (Array.isArray(subscribedIds) && subscribedIds.length > 0) {
        const defaultServiceIds = defaultNewslettersValue.map(
          value => value.serviceId,
        );
        const allDefaultSubscribed = defaultServiceIds.every(id =>
          subscribedIds.includes(id),
        );
        if (allDefaultSubscribed) {
          setShowUnsubscribeAllBtn(true);
        } else {
          setShowUnsubscribeAllBtn(false);
        }
      }
    },
    [subscribedIds],
  );

  return (
    <div className={s.pageWrap}>
      {!isListingPage && (
        <NewsletterBanner
          subscribeAllCallback={subscribeAllNewletters}
          unsubscribeAllCallback={unsubscribeAllNewletters}
          showUnsubscribeAllBtn={showUnsubscribeAllBtn}
        />
      )}
      <div
        className={`${s.cardWrapper} ${
          isListingPage ? s.listingPageNewsletterWidget : ''
        }`}
      >
        {newslettersData.map(newsletter => (
          <NewsletterCard
            key={newsletter.msid}
            {...newsletter}
            isSubscribed={subscribedIds.includes(newsletter.serviceId)}
            onSubscribe={onSubscribe}
            userPreference={newsletter.userPreference}
            isListingPage={isListingPage}
          />
        ))}
      </div>
      {showRequestLimitExceedPopup && (
        <NewsletterSubscriptionPopup
          heading="Limit Reached"
          thanksMessage="You've subscribed to this newsletter 3 times today. Try again tomorrow."
          onCloseCallback={() => setShowRequestLimitExceedPopup(false)}
          infoPopup
        />
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  userData:
    state.login && state.login.userData ? state.login.userData : undefined,
});

const mapDispatchToProps = dispatch => ({
  toggleLoginBox: (isOpen, userEmailid) => {
    dispatch(toggleLoginBox(isOpen, userEmailid));
  },
});
export default connect(mapStateToProps, mapDispatchToProps)(Newsletter);
